import React from 'react';
import { noop } from 'lodash';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { RenderProp } from 'components/Utilities';
import { ToggleableContext } from 'containers/Toggleable';
import { LanguageString, LanguageContext } from 'containers/LanguageProvider';
import { NewButton as Button } from 'components/Button';
import { FormContext } from 'components/Form';
import { Pill } from 'components/Misc';
import { renderProp } from 'utils/react';
import { itemPropTypes } from '../propTypes';
import styles from './SelectToggle.module.scss';

const SelectToggle = ({
    innerRef,
    selection,
    disabled,
    placeholder,
    className,
    renderSelection,
    selectedItemsText,
    multiple,
    showSelectedCount,
    children,
    ...rest
}) => {
    const { isOpen } = React.useContext(ToggleableContext);
    const { formField, isLargeFormField } = React.useContext(FormContext);
    const { getString } = React.useContext(LanguageContext);

    const renderSelectionLocal = () => {
        if (!Array.isArray(selection) || selection.length === 0) {
            return selection?.label || placeholder;
        }
        if (selection.length === 1) {
            return selection[0]?.label || placeholder;
        }
        if (selection.length > 1) {
            return (
                <LanguageString
                    name="input.select.selectedItems"
                    value={selectedItemsText}
                    count={selection.length}
                />
            );
        }
        return placeholder;
    };

    if (formField && isLargeFormField) {
        return (
            <Button
                ref={innerRef}
                disabled={disabled}
                variant={Button.VARIANT.SECONDARY}
                icon={Button.ICON.CARET_DOWN}
                fullWidth={Button.FULL_WIDTH.NEVER}
                iconClassName={cx('g-button-icon-right')}
                className={className}
                {...rest}
            >
                <RenderProp render={renderSelection} argument={{ selection }}>
                    {() => <span>{renderSelectionLocal()}</span>}
                </RenderProp>
                {children}
            </Button>
        );
    }

    const classNames = cx(
        'btn dropdown-toggle',
        {
            show: isOpen && !disabled,
            disabled,
            placeholder: !selection || selection.length === 0,
        },
        className,
    );

    const pillClassNames = cx(styles.pill, disabled && styles.pillDisabled);
    const shouldShowSummaryPill = multiple && showSelectedCount && selection.length > 0;

    return (
        <button
            ref={innerRef}
            type="button"
            disabled={disabled}
            className={classNames}
            aria-label={
                shouldShowSummaryPill
                    ? getString('input.select.selectedSummary', undefined, {
                          selection: renderProp(renderSelection, renderSelectionLocal(), {
                              selection,
                          }),
                          amount: selection.length,
                      })
                    : undefined
            }
            {...rest}
        >
            <RenderProp render={renderSelection} argument={{ selection }}>
                {() => <span>{renderSelectionLocal()}</span>}
            </RenderProp>
            {children}
            {shouldShowSummaryPill && (
                <Pill aria-hidden value={selection.length} className={pillClassNames} />
            )}
        </button>
    );
};

SelectToggle.displayName = 'SelectToggle';

SelectToggle.propTypes = {
    innerRef: PropTypes.ref(),
    selection: PropTypes.oneOfType([
        PropTypes.shape(itemPropTypes),
        PropTypes.arrayOf(PropTypes.shape(itemPropTypes)),
    ]),
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    renderSelection: PropTypes.func,
    selectedItemsText: PropTypes.string,
    multiple: PropTypes.bool,
    showSelectedCount: PropTypes.bool,
    className: PropTypes.string,
    children: PropTypes.node,
};

SelectToggle.defaultProps = {
    innerRef: null,
    selection: null,
    placeholder: null,
    disabled: false,
    renderSelection: noop,
    selectedItemsText: null,
    multiple: false,
    showSelectedCount: false,
    className: '',
    children: null,
};

export default SelectToggle;
